import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SearchBar } from "@/components/SearchBar";

const EASE = [0.22, 1, 0.36, 1];
const QUICK = ["Cables and Wires", "Switchgear", "Lighting", "Electrical Accessories", "Automation Products"];

const Stat = ({ value, label, loading, testId }) => (
  <div className="text-left">
    {loading ? <span className="ph2-shimmer block h-8 w-12 rounded-lg" /> : <p className="font-display text-2xl font-extrabold text-ph-ink md:text-3xl" data-testid={testId}>{value}</p>}
    <p className="mt-1 font-mono-ph text-[11px] uppercase tracking-[0.18em] text-ph-muted">{label}</p>
  </div>
);

/** Brand strip: where the intro's constellation settles. Real brand records only. */
function BrandStrip({ brands }) {
  if (!brands.length) return null;
  return (
    <nav aria-label="Brands" className="ph-noscroll mt-10 flex gap-2 overflow-x-auto pb-1" data-testid="hero-brand-strip">
      {brands.slice(0, 16).map((b) => (
        <Link key={b.slug} to={`/brands/${b.slug}`} data-testid={`hero-brand-${b.slug}`}
          className="shrink-0 rounded-full border border-ph-line bg-ph-surface px-4 py-2 font-display text-sm font-bold tracking-wide text-ph-ink transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-ph-accent/60 hover:text-ph-accent">
          {b.name}
        </Link>
      ))}
    </nav>
  );
}

export function HeroV2({ brands = [], loading = false, play = true }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const popular = [...brands].sort((a, b) => (b.search_count + b.download_count) - (a.search_count + a.download_count));
  const docs = brands.reduce((s, b) => s + (b.doc_count || 0), 0);
  const show = (d = 0) => ({ initial: false, animate: play ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }, transition: { duration: 0.6, delay: d, ease: EASE } });
  return (
    <section className="relative px-5 pb-14 pt-28 md:pb-20 md:pt-36" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="ph2-grid absolute inset-0" />
        <div className="absolute -left-32 top-16 h-[380px] w-[380px] rounded-full bg-ph-violet/15 blur-[120px]" />
      </div>
      <div className="relative z-10 mx-auto w-full max-w-6xl">
        <motion.h1 {...show(0)} className="font-display text-4xl font-extrabold leading-[1.02] tracking-[-0.035em] text-ph-ink sm:text-5xl lg:text-6xl">
          Every brand. <span className="ph2-lime-text">Every price list.</span>
        </motion.h1>
        <motion.p {...show(0.08)} className="mt-4 max-w-2xl text-base leading-relaxed text-ph-muted md:text-lg">
          An independent directory of electrical-industry price lists, from cables and switchgear to lighting and automation. Find the brand, check the effective date, preview and download.
        </motion.p>
        <motion.div {...show(0.16)} className="relative z-20 mt-7 max-w-3xl">
          <SearchBar query={query} setQuery={setQuery} inputRef={inputRef} popular={popular} />
          <div className="mt-4 flex flex-wrap gap-2" aria-label="Category shortcuts">
            {QUICK.map((c) => (
              <Link key={c} to={`/brands?category=${encodeURIComponent(c)}`} data-testid={`hero-quick-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex min-h-[40px] items-center rounded-full border border-ph-line bg-ph-surface/70 px-3.5 text-sm text-ph-muted transition-[color,border-color] duration-300 hover:border-ph-accent/50 hover:text-ph-ink">
                {c}
              </Link>
            ))}
          </div>
        </motion.div>
        <div className="mt-8 flex gap-10 border-t border-ph-line pt-6" data-testid="hero-stats">
          <Stat value={brands.length} label="Brands" loading={loading} testId="hero-brand-count" />
          <Stat value={docs} label="Published price lists" loading={loading} testId="hero-doc-count" />
        </div>
        <motion.div {...show(0.3)}><BrandStrip brands={brands} /></motion.div>
      </div>
    </section>
  );
}
